import React, { useState, useEffect } from 'react';
import axios from '../api/axiosClient';

function EvidenceReview() {
  const [pendingEvidence, setPendingEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [reviewForm, setReviewForm] = useState({
    status: 'approved',
    comments: ''
  });
  const [filterCompany, setFilterCompany] = useState('');
  const [filterStandard, setFilterStandard] = useState('');
  const [clients, setClients] = useState([]);
  const [isoStandards, setIsoStandards] = useState([]);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    loadData();
    loadFilters();
  }, [filterCompany, filterStandard]);

  const loadFilters = async () => {
    try {
      const [clientsRes, standardsRes] = await Promise.all([
        axios.get('/clients'),
        axios.get('/iso-standards')
      ]);
      // Both endpoints may return paginated { items: [...] } or plain arrays
      const clientData = clientsRes.data;
      const stdData = standardsRes.data;
      setClients(Array.isArray(clientData) ? clientData : (clientData?.items || []));
      setIsoStandards(Array.isArray(stdData) ? stdData : (stdData?.items || []));
    } catch (err) {
      console.error('Error loading filters:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      await loadPendingEvidence();
    } catch (err) {
      console.error('Error loading evidence:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingEvidence = async () => {
    try {
      const params = {};
      if (filterCompany) params.company_id = filterCompany;
      if (filterStandard) params.iso_standard_id = filterStandard;

      const response = await axios.get('/evidence/pending-review', { params });
      setPendingEvidence(response.data);
    } catch (err) {
      console.error('Error loading pending evidence:', err);
      setPendingEvidence([]);
    }
  };

  const handleReview = async (evidenceId) => {
    try {
      await axios.post(`/evidence/${evidenceId}/review`, reviewForm);
      alert(`Evidence ${reviewForm.status} successfully!`);
      setSelectedEvidence(null);
      setReviewForm({ status: 'approved', comments: '' });
      loadData();
    } catch (err) {
      console.error('Error reviewing evidence:', err);
      alert('Failed to submit review: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDownload = async (evidenceId, fileName) => {
    try {
      const response = await axios.get(`/evidence/download/${evidenceId}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading evidence:', err);
      alert('Failed to download file');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: '#ffc107',
      approved: '#28a745',
      rejected: '#dc3545',
      requires_clarification: '#17a2b8'
    };

    const labels = {
      pending: 'Pending Review',
      approved: 'Approved',
      rejected: 'Rejected',
      requires_clarification: 'Needs Clarification'
    };

    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        background: colors[status] || '#6c757d',
        color: 'white',
        display: 'inline-block'
      }}>
        {labels[status] || status}
      </span>
    );
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return mb < 1 ? `${Math.round(bytes / 1024)} KB` : `${mb.toFixed(2)} MB`;
  };

  const renderEvidenceTable = (evidenceList) => {
    if (loading) {
      return <p style={{ textAlign: 'center', color: '#666' }}>Loading evidence...</p>;
    }

    if (evidenceList.length === 0) {
      return (
        <p style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
          No evidence submissions found.
        </p>
      );
    }

    return (
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #dee2e6', background: '#f8f9fa' }}>
            <th style={{ padding: '12px', textAlign: 'left' }}>Client</th>
            <th style={{ padding: '12px', textAlign: 'left' }}>ISO Standard</th>
            <th style={{ padding: '12px', textAlign: 'left' }}>Clause</th>
            <th style={{ padding: '12px', textAlign: 'left' }}>Evidence Title</th>
            <th style={{ padding: '12px', textAlign: 'left' }}>File</th>
            <th style={{ padding: '12px', textAlign: 'left' }}>Submitted</th>
            <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
            <th style={{ padding: '12px', textAlign: 'center' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {evidenceList.map((evidence) => (
            <tr
              key={evidence.id}
              style={{
                borderBottom: '1px solid #dee2e6',
                background: selectedEvidence?.id === evidence.id ? '#e7f3ff' : 'white'
              }}
            >
              <td style={{ padding: '12px' }}>
                <div style={{ fontWeight: '500' }}>{evidence.company_name}</div>
                {evidence.submitted_by_name && (
                  <div style={{ fontSize: '12px', color: '#666' }}>by {evidence.submitted_by_name}</div>
                )}
              </td>
              <td style={{ padding: '12px', fontSize: '14px' }}>{evidence.iso_standard}</td>
              <td style={{ padding: '12px' }}>
                <div style={{ fontWeight: '500' }}>{evidence.clause_number}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>{evidence.clause_title}</div>
              </td>
              <td style={{ padding: '12px' }}>
                <div style={{ fontWeight: '500' }}>{evidence.evidence_title}</div>
                {evidence.evidence_description && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    {evidence.evidence_description}
                  </div>
                )}
              </td>
              <td style={{ padding: '12px' }}>
                <div style={{ fontSize: '13px' }}>{evidence.file_name}</div>
                <div style={{ fontSize: '11px', color: '#999' }}>{formatFileSize(evidence.file_size)}</div>
              </td>
              <td style={{ padding: '12px', fontSize: '13px', color: '#666' }}>
                {new Date(evidence.submission_date).toLocaleDateString()}
              </td>
              <td style={{ padding: '12px' }}>{getStatusBadge(evidence.status)}</td>
              <td style={{ padding: '12px', textAlign: 'center' }}>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  <button
                    onClick={() => handleDownload(evidence.id, evidence.file_name)}
                    style={{
                      padding: '6px 12px',
                      background: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Download
                  </button>
                  {evidence.status === 'pending' && (
                    <button
                      onClick={() => setSelectedEvidence(evidence)}
                      style={{
                        padding: '6px 12px',
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Review
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <h1>Evidence Review</h1>
          <p style={{ color: '#666', margin: 0 }}>
            Review and approve client evidence submissions • {pendingEvidence.length} pending
            {user.role === 'super_admin' && <span style={{ marginLeft: '10px', color: '#007bff' }}>• Viewing ALL cert bodies</span>}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        background: '#f8f9fa',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '15px'
      }}>
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '14px' }}>
            Filter by Client
          </label>
          <select
            value={filterCompany}
            onChange={(e) => {
              setFilterCompany(e.target.value);
              loadData();
            }}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          >
            <option value="">All Clients</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>{client.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '14px' }}>
            Filter by ISO Standard
          </label>
          <select
            value={filterStandard}
            onChange={(e) => {
              setFilterStandard(e.target.value);
              loadData();
            }}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          >
            <option value="">All Standards</option>
            {isoStandards.map(std => (
              <option key={std.id} value={std.id}>{std.code} - {std.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Evidence Table */}
      <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        {renderEvidenceTable(pendingEvidence)}
      </div>

      {/* Review Modal */}
      {selectedEvidence && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginTop: 0 }}>Review Evidence</h2>

            <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ marginBottom: '10px' }}>
                <strong>Client:</strong> {selectedEvidence.company_name}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>ISO Standard:</strong> {selectedEvidence.iso_standard}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Clause:</strong> {selectedEvidence.clause_number} - {selectedEvidence.clause_title}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Evidence Title:</strong> {selectedEvidence.evidence_title}
              </div>
              {selectedEvidence.evidence_description && (
                <div style={{ marginBottom: '10px' }}>
                  <strong>Description:</strong> {selectedEvidence.evidence_description}
                </div>
              )}
              <div style={{ marginBottom: '10px' }}>
                <strong>File:</strong> {selectedEvidence.file_name} ({formatFileSize(selectedEvidence.file_size)})
              </div>
              <div>
                <strong>Submitted:</strong> {new Date(selectedEvidence.submission_date).toLocaleString()}
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Decision *</label>
              <select
                value={reviewForm.status}
                onChange={(e) => setReviewForm({ ...reviewForm, status: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '6px'
                }}
              >
                <option value="approved">Approve - Evidence meets requirements</option>
                <option value="rejected">Reject - Evidence does not meet requirements</option>
                <option value="requires_clarification"> Requires Clarification - Need more information</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                Comments * {reviewForm.status !== 'approved' && '(Required for rejection/clarification)'}
              </label>
              <textarea
                value={reviewForm.comments}
                onChange={(e) => setReviewForm({ ...reviewForm, comments: e.target.value })}
                rows="4"
                placeholder="Provide feedback to the client about this evidence submission..."
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setSelectedEvidence(null);
                  setReviewForm({ status: 'approved', comments: '' });
                }}
                style={{
                  padding: '10px 20px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleReview(selectedEvidence.id)}
                disabled={!reviewForm.comments && reviewForm.status !== 'approved'}
                style={{
                  padding: '10px 20px',
                  background: reviewForm.status === 'approved' ? '#28a745' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: reviewForm.comments || reviewForm.status === 'approved' ? 'pointer' : 'not-allowed',
                  opacity: reviewForm.comments || reviewForm.status === 'approved' ? 1 : 0.5
                }}
              >
                Submit Review
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EvidenceReview;
